import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONCODE_LOAD,
    ACTION_INSTITUTIONCODE_LOAD_BY_ID,
    ACTION_INSTITUTIONCODE_LOAD_BY_INSTITUTIONID,
    ACTION_INSTITUTIONCODE_LOAD_FAILURE,
    ACTION_INSTITUTIONCODE_LOAD_SUCCESS,

    ACTION_INSTITUTIONCODE_SAVE,
    ACTION_INSTITUTIONCODE_SAVE_SUCCESS,
    ACTION_INSTITUTIONCODE_SAVE_FAILURE,

    ACTION_INSTITUTIONCODE_CREATE,
    ACTION_INSTITUTIONCODE_CREATE_SUCCESS,
    ACTION_INSTITUTIONCODE_CREATE_FAILURE,

    ACTION_INSTITUTIONCODE_VALIDATE,
    ACTION_INSTITUTIONCODE_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONCODE_VALIDATE_FAILURE,

    ACTION_INSTITUTIONCODE_CLEAR,
    ACTION_INSTITUTIONCODE_FILTER,

    InstitutionCodeAction,
} from '../actions/institutionCode';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionCodeState {
    formData: Dtos.InstitutionCode | Dtos.InstitutionCode[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionCode[];
    formLength: number;
}

const initialState: IInstitutionCodeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionCodeState, _isLoading: boolean): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IInstitutionCodeState,
    formData: Dtos.InstitutionCode | Dtos.InstitutionCode[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IInstitutionCodeState, formFilter: IFormFilter): IInstitutionCodeState {

    const items: Dtos.InstitutionCode[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.codeValue.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IInstitutionCodeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IInstitutionCodeState, responseStatus: Dtos.ResponseStatus): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IInstitutionCodeState, propertiesToClear?: string[]): IInstitutionCodeState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IInstitutionCodeState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionCodeState, _isSaving: boolean): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionCodeState,
    formData: Dtos.InstitutionCode | Dtos.InstitutionCode[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionCodeState, responseStatus: Dtos.ResponseStatus): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionCodeState, _isCreating: boolean): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionCodeState,
    formData: Dtos.InstitutionCode,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionCodeState, responseStatus: Dtos.ResponseStatus): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IInstitutionCodeState, _isValidating: boolean): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionCodeState,
    formData: Dtos.InstitutionCode,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionCodeState, responseStatus: Dtos.ResponseStatus): IInstitutionCodeState {
    const newState: IInstitutionCodeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const InstitutionCodeReducer: Reducer<IInstitutionCodeState> = (state: IInstitutionCodeState = initialState, action: InstitutionCodeAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONCODE_LOAD:
        case ACTION_INSTITUTIONCODE_LOAD_BY_ID:
        case ACTION_INSTITUTIONCODE_LOAD_BY_INSTITUTIONID:
            return loading(state, true);
        case ACTION_INSTITUTIONCODE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONCODE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONCODE_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONCODE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCODE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODE_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONCODE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONCODE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODE_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONCODE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCODE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default InstitutionCodeReducer;